import React, { useEffect, useState } from "react";
import {
     Box,
     IconButton,
     Tooltip,
     Badge,
     Menu,
     MenuItem,
     Typography,
     Divider,
     Button,
     ListItemIcon,
     ListItem,
     useTheme,
} from "@mui/material";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import { getNotifications, getUnreadCount, markNotificationRead } from "../../services/APIServices/notification.api";
import { Check, MoreVert } from "@mui/icons-material";
import moment from "moment";
import { useAppSelector } from "../../store/hooks";
import { getUser } from "../../store/reducers/user.reducer";
import logoOnly from "../../assets/IndeeFlix-logo-only.png";
import { getVideoDetailsByID } from "../../services/APIServices/videos.api";
import { useNavigate } from "react-router-dom";
import { PAGE_ROUTES } from "../../utils/constants";
import { useTranslation } from "react-i18next";

const NotificationMenu = () => {
     const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
     const [notifications, setNotifications] = useState([]);
     const [unreadCount, setUnreadCount] = useState(0);
     const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
     const [selectedNotification, setSelectedNotification] = useState<number | null>(null);
     const [visibleNotifications, setVisibleNotifications] = useState<string[]>([]);
     const theme = useTheme();
     const navigate = useNavigate();
     const user = useAppSelector((state) => getUser(state));
     const { t } = useTranslation();

     useEffect(() => {
          if (user?.userID) {
               fetchNotifications();
               fetchUnreadCount();
          }
     }, []);

     const fetchNotifications = async () => {
          try {
               const response = await getNotifications();
               setNotifications(response as any || []);
          } catch (error) {
               console.error("Error fetching notifications", error);
          }
     };

     const fetchUnreadCount = async () => {
          try {
               const response = await getUnreadCount();
               const data = response as any;
               setUnreadCount(data || 0);
          } catch (error) {
               console.error("Error fetching unread count", error);
          }
     };

     const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
          setAnchorEl(event.currentTarget);
     
         const notificationIds = notifications
             ?.filter((notification: any) => !notification.read)
             ?.map((notification: any) => notification.id);
     
         setVisibleNotifications(notificationIds || []);
     };
     
     const handleMenuClose = async () => {
          setAnchorEl(null);
     
         if (visibleNotifications.length > 0) {
          await Promise.all(visibleNotifications.map((id: any) => markNotificationRead(id)));
  
          fetchNotifications();
          fetchUnreadCount();
      }
     };     

     const handleMoreOptions = (event: React.MouseEvent<HTMLElement>, id: number) => {
          setMenuAnchor(event.currentTarget);
          setSelectedNotification(id);
     };

     const handleMarkAsRead = async () => {
          if (selectedNotification !== null) {
               await markNotificationRead(selectedNotification);
               fetchNotifications();
               fetchUnreadCount();
               setMenuAnchor(null);
          }
     };

     const handleNotificationClick = (notification: any) => {

          const videoId = notification.videoId;
          if (notification.gotoScreen && videoId) {
               getVideoDetailsByID(videoId).then((res: any) => {
                    if (res?.videoType === "short") {
                         navigate(`${PAGE_ROUTES.SHORT_VIDEO_PAGE}/${videoId}`);
                    } else if (res?.videoType === "full") {
                         navigate(`${PAGE_ROUTES.PLAYER_PAGE}/${videoId}`)
                    }
               });
          }
     };

     return (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
               <Tooltip title={t("notification")} arrow>
                    <IconButton size="large" color="inherit" onClick={handleMenuOpen}>
                         <Badge badgeContent={unreadCount} color="error" invisible={unreadCount === 0}>
                              <NotificationsRoundedIcon />
                         </Badge>
                    </IconButton>
               </Tooltip>

               <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    PaperProps={{
                         sx: {
                              width: 350,
                              maxHeight: 400,
                              borderRadius: 2,
                              display: "flex",
                              paddingTop: 0,
                              paddingBottom: 0,
                              zIndex: 50,
                              flexDirection: "column",
                              backgroundColor: theme.palette.mode === "dark" ? "#121212" : "white",
                              color: theme.palette.mode === "dark" ? "#ffffff" : "black",
                              "& .MuiList-root": {
                                   paddingTop: 0,
                                   paddingBottom: 0,
                              }
                         },
                    }}
               >
                    {/* Sticky Header */}
                    <Box sx={{
                         position: "sticky",
                         top: 0,
                         backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "white",
                         zIndex: 55,
                         borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#333" : "#ddd"}`,
                         height: "50px",
                         display: "flex",
                         alignItems: "center",
                         justifyContent: "center",
                         padding: "0px",
                         margin: "0px"
                    }}>
                         <Typography variant="h6" sx={{ fontWeight: "bold", color: "#0046AE" }}>
                              Notifications
                         </Typography>
                    </Box>

                    <Divider sx={{ backgroundColor: theme.palette.mode === "dark" ? "#333" : "#ddd" }} />

                    {/* Scrollable Content */}
                    <Box sx={{
                         maxHeight: 350,
                         overflowY: "auto",
                         padding: 0,
                         margin: 0,
                    }}>
                         {notifications.length === 0 ? (
                              <MenuItem disabled sx={{ justifyContent: "center", py: 2, color: theme.palette.text.secondary }}>
                                   <Typography variant="body2" color="textSecondary">
                                        No notifications
                                   </Typography>
                              </MenuItem>
                         ) : (
                              notifications.map((notification: any) => (
                                   <MenuItem
                                        key={notification.id}
                                        sx={{
                                             display: "flex",
                                             alignItems: "center",
                                             px: 2,
                                             py: 1.2,
                                             bgcolor: notification.read
                                                  ? "inherit"
                                                  : theme.palette.mode === "dark" ? "#333" : "#f5f5f5",
                                             "&:hover": {
                                                  bgcolor: theme.palette.mode === "dark" ? "#444" : "#e0e0e0",
                                             },
                                        }}
                                        onClick={() => handleNotificationClick(notification)}
                                   >

                                        {/* Notification Message & Time */}
                                        <Box sx={{ flex: 1, pr: 1 }}>
                                             <Typography
                                                  variant="body2"
                                                  sx={{
                                                       fontWeight: notification.read ? "normal" : "bold",
                                                       display: "-webkit-box",
                                                       WebkitBoxOrient: "vertical",
                                                       WebkitLineClamp: 3,
                                                       overflow: "hidden",
                                                       textOverflow: "ellipsis",
                                                       whiteSpace: "normal",
                                                       wordBreak: "break-word",
                                                       maxHeight: "4.5em",
                                                  }}
                                             >
                                                  {notification.message}
                                             </Typography>
                                             <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                                  {moment(`${notification.createdAt?.split("T")?.join(" ")} UTC`).fromNow()}
                                             </Typography>
                                        </Box>

                                        {/* Thumbnail Image */}
                                        <Box sx={{
                                             width: 40,
                                             height: 40,
                                             minWidth: 40,
                                             borderRadius: 1,
                                             overflow: "hidden",
                                             mr: 2,
                                        }}>
                                             {notification.thumbnail && (
                                                  <img
                                                       src={notification.thumbnail || ''}
                                                       alt="Thumbnail"
                                                       style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "cover",
                                                       }}
                                                  />
                                             )}
                                        </Box>

                                        {/* More Options Button */}
                                        {/* <IconButton size="small" onClick={(e) => handleMoreOptions(e, notification.id)}>
                                             <MoreVert fontSize="small" sx={{ color: theme.palette.text.primary }} />
                                        </IconButton> */}
                                   </MenuItem>
                              ))
                         )}
                    </Box>
               </Menu>



               {/* More Options Menu */}
               <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    PaperProps={{
                         sx: {
                              borderRadius: 2,
                              backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "white",
                              color: theme.palette.mode === "dark" ? "#ffffff" : "black",
                         },
                    }}
               >
                    <MenuItem onClick={handleMarkAsRead}>
                         <ListItem>
                              <Check fontSize="small" sx={{ color: theme.palette.text.primary }} />
                         </ListItem>
                         Mark as Read
                    </MenuItem>
               </Menu>
          </Box>
     );
};

export default NotificationMenu;
